import { create } from 'zustand';
import type { ValidationIssue } from '../types';
import { withSectionSpacing } from '../lib/reportClipboard';

interface ReportState {
  // Current session
  transcription: string;
  rawReport: string;  // Original LLM output (for debug view)
  generatedReport: string;  // Processed report
  editedReport: string;
  issues: ValidationIssue[];
  modality: string;
  bodyPart: string;
  clinicalHistory: string;
  patientIdentifier: string;
  studyDate: string;
  selectedModel: string;

  // User-chosen template override (manual selection, shared across the
  // dictation panel and the docked template rail). Distinct from the
  // classification result (templateId/templateName) below.
  selectedTemplateId: string;
  selectedTemplateName: string;

  // Whether the docked (right-hand) template panel is visible. Single
  // templates surface — replaces the old full-screen modal.
  templatesPanelOpen: boolean;

  // Whether the docked issues panel (dictation gaps + validation) is visible.
  // Auto-opens when a generation returns issues; never modifies the report.
  issuesPanelOpen: boolean;

  // Debug info
  debugPrompt: string;  // System prompt sent to OpenAI
  debugUserMessage: string;  // User message sent to OpenAI

  // Template classification info
  examCategory: string;
  templateId: string;
  templateName: string;
  decisionId: string;
  classificationMethod: string;
  classificationConfidence: number;

  // Fallback chain info — populated by the most recent generation
  modelUsed: string;     // model that actually produced the report
  primaryModel: string;  // configured primary (target of "retry with primary")

  // UI state
  isRecording: boolean;
  isTranscribing: boolean;
  isGenerating: boolean;
  highlightedIssue: ValidationIssue | null;
  showDebugView: boolean;  // Toggle for two-pane debug view
  centerView: 'editor' | 'debug' | 'compare';  // 3-way view selector
  editorTab: 'write' | 'preview';

  // History for undo/redo
  reportHistory: string[];
  historyIndex: number;

  // Bumped by resetSession ("New Report") so the recorder/mic in DictationPanel
  // can tear down any in-progress capture (the recorder lives in a hook, not
  // the store, so clearing state alone doesn't stop it).
  resetNonce: number;

  // Actions
  setTranscription: (text: string) => void;
  setRawReport: (text: string) => void;
  setGeneratedReport: (text: string) => void;
  setEditedReport: (text: string) => void;
  setIssues: (issues: ValidationIssue[]) => void;
  setModality: (modality: string) => void;
  setBodyPart: (bodyPart: string) => void;
  setClinicalHistory: (history: string) => void;
  setPatientIdentifier: (id: string) => void;
  setStudyDate: (date: string) => void;
  setSelectedModel: (model: string) => void;
  setSelectedTemplate: (id: string, name: string) => void;
  setTemplatesPanelOpen: (open: boolean) => void;
  setIssuesPanelOpen: (open: boolean) => void;
  setIsRecording: (isRecording: boolean) => void;
  setIsTranscribing: (isTranscribing: boolean) => void;
  setIsGenerating: (isGenerating: boolean) => void;
  setHighlightedIssue: (issue: ValidationIssue | null) => void;
  setShowDebugView: (show: boolean) => void;
  setCenterView: (view: 'editor' | 'debug' | 'compare') => void;
  setEditorTab: (tab: 'write' | 'preview') => void;
  setDebugPrompt: (prompt: string) => void;
  setDebugUserMessage: (message: string) => void;

  // Template classification setters
  setExamCategory: (category: string) => void;
  setTemplateId: (id: string) => void;
  setTemplateName: (name: string) => void;
  setDecisionId: (id: string) => void;
  setClassificationMethod: (method: string) => void;
  setClassificationConfidence: (confidence: number) => void;

  // Fallback chain setters
  setModelUsed: (model: string) => void;
  setPrimaryModel: (model: string) => void;

  // Undo/Redo
  pushToHistory: (text: string) => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  // Issue management
  resolveIssue: (issueId: string) => void;
  applyFix: (issueId: string) => void;

  // Reset
  resetSession: () => void;
}

const initialState = {
  transcription: '',
  rawReport: '',
  generatedReport: '',
  editedReport: '',
  issues: [],
  modality: 'CT',
  bodyPart: '',
  clinicalHistory: '',
  patientIdentifier: '',
  studyDate: '',
  selectedModel: 'claude-opus-4-6',
  selectedTemplateId: '',
  selectedTemplateName: '',
  templatesPanelOpen: true,
  issuesPanelOpen: false,
  debugPrompt: '',
  debugUserMessage: '',
  examCategory: '',
  templateId: '',
  templateName: '',
  decisionId: '',
  classificationMethod: '',
  classificationConfidence: 0,
  modelUsed: '',
  primaryModel: '',
  isRecording: false,
  isTranscribing: false,
  isGenerating: false,
  highlightedIssue: null,
  showDebugView: false,
  centerView: 'editor' as const,
  editorTab: 'write' as const,
  reportHistory: [],
  historyIndex: -1,
  resetNonce: 0,
};

export const useReportStore = create<ReportState>()((set, get) => ({
  ...initialState,

  setTranscription: (text) => set({ transcription: text }),

  setRawReport: (text) => set({ rawReport: text }),

  setGeneratedReport: (text) => {
    // Materialise a blank line between sections as REAL empty paragraphs so it
    // survives native Ctrl+C into Karisma (which pastes the HTML flavor). See
    // withSectionSpacing — this is the auto-equivalent of pressing Enter before
    // each section heading.
    const spaced = withSectionSpacing(text);
    set({
      generatedReport: spaced,
      editedReport: spaced,
      reportHistory: [spaced],
      historyIndex: 0,
    });
  },

  setEditedReport: (text) => set({ editedReport: text }),

  setIssues: (issues) => set({ issues }),

  setModality: (modality) => set({ modality, bodyPart: '' }),

  setBodyPart: (bodyPart) => set({ bodyPart }),

  setClinicalHistory: (clinicalHistory) => set({ clinicalHistory }),

  setPatientIdentifier: (patientIdentifier) => set({ patientIdentifier }),

  setStudyDate: (studyDate) => set({ studyDate }),

  setSelectedModel: (selectedModel) => set({ selectedModel }),

  setSelectedTemplate: (selectedTemplateId, selectedTemplateName) =>
    set({ selectedTemplateId, selectedTemplateName }),

  setTemplatesPanelOpen: (templatesPanelOpen) => set({ templatesPanelOpen }),

  setIssuesPanelOpen: (issuesPanelOpen) => set({ issuesPanelOpen }),

  setIsRecording: (isRecording) => set({ isRecording }),

  setIsTranscribing: (isTranscribing) => set({ isTranscribing }),

  setIsGenerating: (isGenerating) => set({ isGenerating }),

  setHighlightedIssue: (highlightedIssue) => set({ highlightedIssue }),

  setShowDebugView: (showDebugView) => set({ showDebugView }),

  setCenterView: (centerView) => set({
    centerView,
    showDebugView: centerView === 'debug',
  }),

  setEditorTab: (editorTab) => set({ editorTab }),

  setDebugPrompt: (debugPrompt) => set({ debugPrompt }),

  setDebugUserMessage: (debugUserMessage) => set({ debugUserMessage }),

  // Template classification setters
  setExamCategory: (examCategory) => set({ examCategory }),
  setTemplateId: (templateId) => set({ templateId }),
  setTemplateName: (templateName) => set({ templateName }),
  setDecisionId: (decisionId) => set({ decisionId }),
  setClassificationMethod: (classificationMethod) => set({ classificationMethod }),
  setClassificationConfidence: (classificationConfidence) => set({ classificationConfidence }),

  // Fallback chain setters
  setModelUsed: (modelUsed) => set({ modelUsed }),
  setPrimaryModel: (primaryModel) => set({ primaryModel }),

  pushToHistory: (text) => {
    const state = get();
    // Remove any redo history
    const newHistory = state.reportHistory.slice(0, state.historyIndex + 1);
    newHistory.push(text);
    // Limit history size
    if (newHistory.length > 50) {
      newHistory.shift();
    }
    set({
      reportHistory: newHistory,
      historyIndex: newHistory.length - 1,
      editedReport: text,
    });
  },

  undo: () => {
    const state = get();
    if (state.historyIndex > 0) {
      const newIndex = state.historyIndex - 1;
      set({
        historyIndex: newIndex,
        editedReport: state.reportHistory[newIndex],
      });
    }
  },

  redo: () => {
    const state = get();
    if (state.historyIndex < state.reportHistory.length - 1) {
      const newIndex = state.historyIndex + 1;
      set({
        historyIndex: newIndex,
        editedReport: state.reportHistory[newIndex],
      });
    }
  },

  canUndo: () => {
    const state = get();
    return state.historyIndex > 0;
  },

  canRedo: () => {
    const state = get();
    return state.historyIndex < state.reportHistory.length - 1;
  },

  resolveIssue: (issueId) => {
    const state = get();
    set({
      issues: state.issues.filter((issue) => issue.id !== issueId),
    });
  },

  applyFix: (issueId) => {
    const state = get();
    const issue = state.issues.find((i) => i.id === issueId);
    if (issue?.auto_fixable && issue.fix_value) {
      // Apply the fix (simplified - in production, need smarter text replacement)
      const newReport = state.editedReport.replace(
        new RegExp(issue.message.split(':')[1]?.trim() || '', 'gi'),
        issue.fix_value
      );
      get().pushToHistory(newReport);
      get().resolveIssue(issueId);
    }
  },

  resetSession: () => set((s) => ({ ...initialState, resetNonce: s.resetNonce + 1 })),
}));
